import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
  ArrowBack,
  QuestionAnswer,
  Check,
  Info,
  Email,
  AccessTime,
  Business,
  Assignment,
  Person,
  Add,
  Warning,
} from '@mui/icons-material';

import Loading from '../../../components/global/loading/Loading';
import Modal from '../../../components/modals/Modal';
import { useInquiry } from '../../../context/InquiryContext';
import translateStatusToPolish from '../../../helperFunctions/translateStatusToPolish';
import { Inquiry, InquiryStatus } from '../../../models/Inquiry';
import { Controller, useForm } from 'react-hook-form';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Confirm from '../../../components/modals/confirmModal/Confirm';

function InquiryPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getInquiry, changeInquiryStatus } = useInquiry();

  const [inquiry, setInquiry] = useState<Inquiry | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      status: inquiry?.status || InquiryStatus.PENDING,
    },
  });

  const selectedStatus = watch('status');

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  useEffect(() => {
    async function fetchInquiry() {
      if (!id) {
        toast.error('Nie znaleziono zapytania');
        navigate('/admin/zapytania');
        return;
      }

      const toastId = toast.loading('Ładowanie zapytania...');
      try {
        const inquiry = await getInquiry(parseInt(id as string));
        setInquiry(inquiry);
        toast.dismiss(toastId);
      } catch (error) {
        toast.error('Nie udało się załadować zapytania');
        navigate('/admin/zapytania');
      }
    }

    fetchInquiry();
  }, [id, getInquiry, navigate]);

  const handleChangeStatus = async () => {
    if (!inquiry) return;

    const toastId = toast.loading('Aktualizowanie statusu zapytania...');
    try {
      await changeInquiryStatus(inquiry.id, selectedStatus);

      toast.update(toastId, {
        render: 'Status zapytania został zmieniony',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      // Odświeżenie danych
      const updatedInquiry = await getInquiry(inquiry.id);
      setInquiry(updatedInquiry);
      setShowConfirmModal(false);
      setShowStatusModal(false);
    } catch (error: any) {
      toast.update(toastId, {
        render:
          error.message || 'Wystąpił błąd podczas zmiany statusu zapytania',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleBackClick = () => {
    navigate('/admin/zapytania');
  };

  // Funkcje dla stylów statusów
  const getInquiryStatusColor = (status: InquiryStatus) => {
    switch (status) {
      case InquiryStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-200';
      case InquiryStatus.IN_PROGRESS:
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case InquiryStatus.CLOSED:
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  const getInquiryStatusIcon = (status: InquiryStatus) => {
    switch (status) {
      case InquiryStatus.COMPLETED:
        return <Check fontSize="small" />;
      case InquiryStatus.IN_PROGRESS:
        return <Info fontSize="small" />;
      case InquiryStatus.CLOSED:
        return <Warning fontSize="small" />;
      default:
        return <QuestionAnswer fontSize="small" />;
    }
  };

  // Formatowanie daty
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Formatowanie produktów
  const getProductsText = (count: number) => {
    if (count === 1) return '1 produkt';
    if (count > 1 && count < 5) return `${count} produkty`;
    return `${count} produktów`;
  };

  if (!inquiry) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  const totalItems =
    inquiry.products?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <motion.div
      className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Przycisk powrotu */}
      <motion.button
        onClick={handleBackClick}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        variants={itemVariants}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <ArrowBack fontSize="small" />
        <span className="font-medium">Powrót</span>
      </motion.button>

      {/* Panel administracyjny */}
      <motion.div
        className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8"
        variants={itemVariants}
      >
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
          <h2 className="text-lg font-medium text-gray-800">
            Panel administracyjny
          </h2>
        </div>

        <div className="p-5 space-y-6">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">
              Akcje administracyjne
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Poniżej znajdziesz przyciski do zarządzania zapytaniem oraz
              informacje krok po kroku, które pomogą Ci w obsłudze zapytania
              klienta.
            </p>

            <div className="flex flex-wrap gap-3 mb-4">
              {inquiry.status === InquiryStatus.PENDING && (
                <motion.button
                  onClick={() => setShowStatusModal(true)}
                  className="px-4 py-2 bg-blue-50 border border-blue-300 text-blue-700 hover:bg-blue-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Info fontSize="small" />
                  Zmień status
                </motion.button>
              )}

              {inquiry.status === InquiryStatus.IN_PROGRESS && (
                <>
                  <motion.button
                    onClick={() => navigate(`/admin/oferty/dodaj/${inquiry.id}`)}
                    className="px-4 py-2 bg-green-50 border border-green-300 text-green-700 hover:bg-green-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Add fontSize="small" />
                    Utwórz ofertę
                  </motion.button>

                  <motion.button
                    onClick={() => setShowStatusModal(true)}
                    className="px-4 py-2 bg-blue-50 border border-blue-300 text-blue-700 hover:bg-blue-100 rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Info fontSize="small" />
                    Zmień status
                  </motion.button>
                </>
              )}

              {inquiry.status === InquiryStatus.COMPLETED && (
                <p className="text-green-700 text-sm">
                  Zapytanie zostało zakończone. Utworzono ofertę dla klienta.
                </p>
              )}

              {inquiry.status === InquiryStatus.CLOSED && (
                <p className="text-red-700 text-sm">
                  Zapytanie zostało zamknięte.
                </p>
              )}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-2">Wskazówki</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>
                    Przejrzyj produkty w zapytaniu, aby lepiej zrozumieć
                    potrzeby klienta.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>
                    Zmień status na "W trakcie realizacji", żeby klient
                    wiedział, że zapytanie jest przetwarzane.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>
                    Po przygotowaniu oferty dla klienta, należy utworzyć ją w
                    systemie korzystając z przycisku "Utwórz ofertę".
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span>
                    Po utworzeniu oferty, status zapytania automatycznie zmieni
                    się na "Zakończone".
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Nagłówek zapytania */}
      <motion.div className="mb-8" variants={itemVariants}>
        <h1 className="text-2xl font-medium text-gray-900 mb-2">
          Zapytanie:{' '}
          <span className="font-bold text-primary">#{inquiry.id}</span>
        </h1>

        <div className="flex flex-wrap gap-3 mt-4">
          <div
            className={`px-3 py-1.5 rounded-full ${getInquiryStatusColor(inquiry.status)} flex items-center gap-1 text-sm font-medium`}
          >
            {getInquiryStatusIcon(inquiry.status)}
            Status: {translateStatusToPolish(inquiry.status)}
          </div>

          <div className="px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700 flex items-center gap-1 text-sm font-medium">
            <AccessTime fontSize="small" />
            Data: {formatDate(inquiry.createdAt)}
          </div>
        </div>
      </motion.div>

      {/* Sekcja główna */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Lewa kolumna - informacje o kliencie */}
        <motion.div variants={itemVariants}>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-base font-medium text-gray-800 mb-3">
                Dane klienta
              </h3>

              <div className="flex items-center mb-4">
                <div className="p-2 bg-gray-50 rounded-md mr-3">
                  {inquiry.nip ? (
                    <Business className="text-gray-500" />
                  ) : (
                    <Person className="text-gray-500" />
                  )}
                </div>
                <div>
                  <div className="font-medium">{inquiry.name}</div>
                  <div className="text-sm text-gray-600">
                    {inquiry.nip ? 'Firma' : 'Osoba prywatna'}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center">
                  <Email fontSize="small" className="text-gray-400 mr-2" />
                  <span className="text-gray-800">{inquiry.email}</span>
                </div>

                {inquiry.nip && (
                  <div className="flex items-center">
                    <Business fontSize="small" className="text-gray-400 mr-2" />
                    <span className="text-gray-800">NIP: {inquiry.nip}</span>
                  </div>
                )}
              </div>
            </div>

            {inquiry.remarks && (
              <div className="p-5 border-b border-gray-100">
                <h3 className="text-base font-medium text-gray-800 mb-3">
                  Dodatkowe uwagi klienta
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                  <p className="italic">"{inquiry.remarks}"</p>
                </div>
              </div>
            )}

            <div className="p-5">
              <h3 className="text-base font-medium text-gray-800 mb-3">
                Podsumowanie
              </h3>
              <div className="space-y-2 text-gray-600">
                <p>
                  <span className="text-gray-500">Liczba produktów:</span>{' '}
                  <span className="font-medium">
                    {getProductsText(inquiry.products?.length || 0)}
                  </span>
                </p>
                <p>
                  <span className="text-gray-500">Łączna ilość:</span>{' '}
                  <span className="font-medium">{totalItems} szt.</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Prawa kolumna - produkty */}
        <motion.div variants={itemVariants}>
          <div className="sticky top-8">
            <h3 className="text-base font-medium text-gray-800 mb-3">
              Produkty w zapytaniu
            </h3>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-100">
                {inquiry.products?.map((product) => (
                  <div
                    key={product.productId}
                    className="p-4 flex items-center gap-4"
                  >
                    <div className="h-16 w-16 bg-gray-50 rounded-md overflow-hidden flex-shrink-0">
                      {product.product?.mobilePrimaryImage ? (
                        <img
                          src={product.product.mobilePrimaryImage}
                          alt={product.product.name}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-gray-400">
                          <Assignment />
                        </div>
                      )}
                    </div>

                    <div className="flex-grow">
                      <Link
                        to={`/produkt/${product.product?.slug}`}
                        className="font-medium text-gray-800 hover:text-primary transition-colors"
                        target="_blank"
                      >
                        {product.product?.name}
                      </Link>
                      <p className="text-sm text-gray-500">
                        Kod produktu: {product.product?.serialNumber || 'N/A'}
                      </p>
                    </div>

                    <div className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-md text-gray-700 font-medium">
                      {product.quantity} szt.
                    </div>
                  </div>
                ))}

                {(!inquiry.products || inquiry.products.length === 0) && (
                  <div className="p-4 text-center text-gray-500">
                    Nie znaleziono produktów w zapytaniu.
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal zmiany statusu */}
      <Modal
        show={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        size="sm"
      >
        <div className="p-5">
          <div className="mb-4 flex items-center gap-2">
            <QuestionAnswer className="text-primary" />
            <h2 className="text-lg font-medium text-gray-900">
              Zmień status zapytania
            </h2>
          </div>

          <form
            onSubmit={handleSubmit(() => setShowConfirmModal(true))}
            className="space-y-6"
          >
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Zmiana statusu zapytania spowoduje wysłanie odpowiedniego
                powiadomienia do klienta.
              </p>

              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Controller
                  name="status"
                  control={control}
                  defaultValue={inquiry.status}
                  render={({ field }) => (
                    <Select {...field} label="Status">
                      <MenuItem value={InquiryStatus.PENDING}>
                        {translateStatusToPolish(InquiryStatus.PENDING)}
                      </MenuItem>
                      <MenuItem value={InquiryStatus.IN_PROGRESS}>
                        {translateStatusToPolish(InquiryStatus.IN_PROGRESS)}
                      </MenuItem>
                      <MenuItem value={InquiryStatus.CLOSED}>
                        {translateStatusToPolish(InquiryStatus.CLOSED)}
                      </MenuItem>
                    </Select>
                  )}
                />
              </FormControl>

              {selectedStatus === InquiryStatus.CLOSED && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  <Warning fontSize="small" className="mr-1" />
                  Uwaga! Zamknięcie zapytania jest nieodwracalne. Klient
                  zostanie poinformowany, że jego zapytanie zostało zamknięte.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <motion.button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                onClick={() => setShowStatusModal(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Anuluj
              </motion.button>

              <motion.button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-md font-medium hover:bg-primary-dark transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Potwierdź zmianę
              </motion.button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Modal potwierdzenia */}
      <Confirm
        message={
          selectedStatus === InquiryStatus.CLOSED
            ? 'Czy na pewno chcesz zamknąć to zapytanie? Ta operacja jest nieodwracalna.'
            : 'Czy na pewno chcesz zmienić status zapytania?'
        }
        onConfirm={handleChangeStatus}
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
      />
    </motion.div>
  );
}

export default InquiryPage;
